/**
 * User-facing Sphere AI chat settings — the switches behind the composer's gear
 * (see SphereAIComposerSettings).
 *
 * ONE atom holding an object, not one atom per switch: the set is expected to grow,
 * and a single persisted key keeps every future addition a field rather than a new
 * storage key to migrate. Reading a field you didn't add yet falls back to the
 * default, so an older persisted value stays valid (see {@link useChatSettings}).
 *
 * Persisted per user, like the dock preference in ./chatDock — not per conversation.
 * A preference the user sets once should still be set tomorrow, and neither switch
 * needs history to change with it: the server decides thinking per TURN from the
 * request body, and both switches are safe to flip mid-thread (the thinking one is
 * pinned against the live API in sphere-mcp's translation.integration.test.ts).
 *
 * The two per-request readers take this atom directly rather than being handed the
 * values, because neither is a React component: SphereAIChatTransport reads `thinking`
 * when it builds each request body, and MCPHttpClient reads `webSearch` when it builds
 * each request's headers — the same lazy per-request read they already use for the
 * access token.
 *
 * `webSearch` has a THIRD reader, and that one does subscribe: SphereAIChatSection keys
 * its MCP tool-discovery query on it, because the server advertises `web-fetch` only
 * when the flag is on and the list is fetched once per mount. A lazy read cannot see a
 * flip, and the flip is exactly what has to re-list.
 */
import { atomWithStorage, readAtom, useAtom } from "@mdotm/shadcn/legacy/reactivity";

export type ChatSettings = {
	/**
	 * Extended thinking. Sent as the chat body's `thinking` field; the server maps it
	 * to Anthropic's adaptive-vs-disabled thinking option (thinkingOptionsFor in
	 * sphere-mcp's chat route).
	 */
	thinking: boolean;
	/**
	 * Web search — the `web-fetch` MCP tool. Sent as the `X-Sphere-Web-Search` header
	 * on every MCP request.
	 *
	 * It can only ever turn the tool OFF. The server's `WEB_SEARCH_ENABLED` is a
	 * ceiling — where a deployment doesn't offer web search, switching this on does
	 * nothing, because a client cannot claim capability the deployment withheld. So
	 * `true` means "don't opt out", not "guarantee web access".
	 */
	webSearch: boolean;
};

/**
 * Thinking on, web search off: the assistant reasons by default, but reaching out to
 * the public web is something the user asks for.
 *
 * These fields are POSITIVE ("thinking is on") because that is what the wire wants —
 * the chat body's `thinking` field and the `X-Sphere-Web-Search` header both say what
 * is enabled — and the popover reads the same way round, every switch there an
 * "Enable …" shown directly. So a fresh chat shows thinking on and web search off.
 */
export const CHAT_SETTINGS_DEFAULTS: ChatSettings = {
	thinking: true,
	webSearch: false,
};

/**
 * Persisted settings. Typed as a PARTIAL because what's in storage was written by
 * whatever version of the app the user last ran: a value persisted before a switch
 * existed is missing that field, and the type has to admit that rather than lie.
 * {@link readChatSettings} is what resolves it to a complete object.
 */
const chatSettings$ = atomWithStorage<Partial<ChatSettings>>("sphereAIChatSettings", {}, undefined, {
	// NOT optional here. `getOnInit` defaults to false, which means the atom holds the
	// initial value (`{}` → every default) until something SUBSCRIBES to it and jotai
	// syncs from storage. Both readers of this atom are plain function calls, not
	// components, and the MCP client's first requests go out while the chat panel — and
	// therefore the popover that would subscribe — has not mounted yet. Without this the
	// session that fixes which tools are advertised is opened with the DEFAULTS rather
	// than the user's saved setting: caught in the browser, not by any type or unit test.
	getOnInit: true,
});

/** The persisted settings with every missing field filled from the defaults. */
export function readChatSettings(): ChatSettings {
	return { ...CHAT_SETTINGS_DEFAULTS, ...readAtom(chatSettings$) };
}

/** The settings and a per-field setter, for the composer's gear popover. */
export function useChatSettings(): {
	settings: ChatSettings;
	setSetting: <K extends keyof ChatSettings>(key: K, value: ChatSettings[K]) => void;
} {
	const [stored, setStored] = useAtom(chatSettings$);
	return {
		settings: { ...CHAT_SETTINGS_DEFAULTS, ...stored },
		setSetting: (key, value) => setStored((prev) => ({ ...prev, [key]: value })),
	};
}
